import { useEffect } from 'react';
import { NEXT_ENTITY } from '@common/interfaces/entity';
import { useClientLazyQuery } from '../useClientLazyQuery';
import { IFilterResult } from 'sales-types-common';

export interface IAuthorisedBrandsParams {
  isAdmin: boolean;
  dealerCode: string;
}

export interface IBrandOption {
  label: string;
  value: string;
}

export interface IAuthorisedBrandsResult {
  brands: string[] | any[];
  brandOptions: IBrandOption[];
  loading: boolean;
  error?: Error;
}

export const useAuthorisedBrands = (params: IAuthorisedBrandsParams): IAuthorisedBrandsResult => {
  const { isAdmin, dealerCode } = params;

  const [getAuthorisedBrands, { data: authorizedBrands, loading: isGetAuthBrandLoading, error: authBrandError }] =
    useClientLazyQuery(NEXT_ENTITY.AUTHORISED_BRAND, {
      variables: {
        isAdmin,
        dealerCode,
      },
    });

  const [getStockVehicleFilters, { data: filterData, loading: filterLoading, error: filterError }] = useClientLazyQuery(
    NEXT_ENTITY.VEHICLE_STOCK_FILTERS,
    {
      variables: {
        filters: {
          filterTypes: ['brand'],
        },
      },
    }
  );

  useEffect(() => {
    if (!dealerCode) {
      return;
    }

    if (isAdmin) {
      getStockVehicleFilters();
    } else {
      getAuthorisedBrands();
    }
  }, [dealerCode, isAdmin]);

  const transformAuthorisedBrands = (brandsList: string[]): IBrandOption[] => {
    return (
      brandsList?.map((brand) => ({
        label: brand,
        value: brand,
      })) ?? []
    );
  };

  const transformFilterBrands = (filterResults: IFilterResult[]): IBrandOption[] => {
    const brandFilter = filterResults?.find((filter) => filter.filterName === 'brand');

    return (
      brandFilter?.filterValues?.map((filterValue: any) => {
        const parts = filterValue.value.split('^');
        const value = parts[1] || parts[0];

        return {
          label: filterValue.code,
          value: value,
        };
      }) ?? []
    );
  };

  const brands = isAdmin ? (filterData?.filterResults ?? []) : (authorizedBrands?.brands ?? []);

  const brandOptions = isAdmin
    ? transformFilterBrands(filterData?.filterResults)
    : transformAuthorisedBrands(authorizedBrands?.brands);

  const loading = isAdmin ? filterLoading : isGetAuthBrandLoading;
  const error = isAdmin ? filterError : authBrandError;

  return {
    brands,
    brandOptions,
    loading,
    error,
  };
};
